<template>

  <div class="starting-message-container">

    <!-- Starting Message Title -->
    <div class="title-box">
      <h4>Starting Message</h4>
      <a-tooltip placement="right">
        <template slot="title">
          <span>This is the first message your respondents will see.</span>
        </template>
        <a-icon type="info-circle" />
      </a-tooltip>
    </div>

    <!-- Starting Message Type Toggle -->
    <div class="toggle-container">
      <div class="toggle-inner">
        <button @click="selected_type='push'" :class="{ selected: selected_type === 'push' }" id="push-message-btn">Push</button>
        <button @click="selected_type='link'" :class="{ selected: selected_type === 'link' }" id="link-message-btn" >Link&nbsp;&nbsp;<span>🔗</span></button>
        <div id="selected-slider" :class="{ link_selected: selected_type === 'link' }"></div>
      </div>
    </div>


    <!-- Push Template Message Editor -->
    <div v-if="selected_type === 'push'" class="push-container">

      <div class="media-row" v-if="push_templates && push_templates[this.selected_push_template_index] && push_templates[this.selected_push_template_index].type === 'image'">

        <!-- Image preview -->
        <div class="media-container" v-if="starting_message.push.image">
          <div class="media-container-overlay" :class="{ darken: starting_message.push.is_uploading }">
            <label v-if="!starting_message.push.is_uploading" class="change-image-btn">
              <input @change="addImage($event)" type="file" ref="file" accept="image/png, image/jpeg" />
            </label>
            <a-spin v-else class="uploading-spinner"><a-icon slot="indicator" type="loading" style="font-size: 40px; color: white;" spin /></a-spin>
          </div>
          <img :src="starting_message.push.image" />
        </div>

      </div>


      <!-- Template Dropdown -->
      <a-dropdown v-if="push_templates && push_templates.length > 1" class="template-dropdown" :trigger="['click']" >
        <a class="ant-dropdown-link" @click="e => e.preventDefault()">
          <button class="select-push-template-btn"/>
        </a>
        <a-menu slot="overlay">
          <a-menu-item v-for="(template, template_index) of push_templates" @click="changePushTemplate(template_index)">
            <a>{{ template.text }}</a>
          </a-menu-item>
        </a-menu>
      </a-dropdown>

      <!-- Template Editor -->
      <div class="push-content">
        <center>
          <template v-for="token of tokenized_push">
            <p v-if="token.type === 'text'">{{ token.text }}</p>
            <template v-else>
              <a-tooltip title="Please enter some text for this" :visible="show_errors && !push_vars[token.var_index]">
                <input
                type="text"
                v-autowidth="{maxWidth: '200px', minWidth: '40px', comfortZone: 6}"
                v-model="push_vars[token.var_index]"
                @input="updateStartingMessagePushVar($event, token.var_index)"
                placeholder=""
                />
              </a-tooltip>
            </template>
          </template>
        </center>
      </div>
    </div>

    <!--  -->

    <!-- QR/Link starting message editor -->
    <template v-else>
      <input
      :value="starting_message.link"
      @input="updateLinkStartingText($event)"
      type="text"
      placeholder="e.g. Hi there, we’d like to ask you some questions about..">
    </template>
  </div>

</template>

<script>

import { db_helper } from '../../store/db_helper.js';
import Compressor from 'compressorjs';

export default {

  name: 'StartingMessage',

  props: {
    show_errors: Boolean,
    is_editing: Boolean
  },

  data: function () {
    return {
      selected_type: 'push',
      selected_push_template_index: 0,
      push_vars: [],
    }
  },

  computed: {

    starting_message() {
      return this.$store.state.new_survey.starting_message;
    },

    push_templates() {
      return this.$store.state.account.push_templates;
    },

    tokenized_push() {

      if (!this.push_templates) { return [] };

      // Tokenize selected push template text
      let selected_push_template = this.push_templates[this.selected_push_template_index];

      let text = selected_push_template.text;
      let chars = text.split('');
      let tokenized_push = [];
      let token = '';
      let var_index = 0;

      for (let i = 0; i < chars.length; i++) {
        let char = chars[i];
        if (char !== '_') {
          token += char;
          if (i === chars.length - 1) {
            tokenized_push.push({
              type: 'text',
              text: token
            });
          };
        } else { // Char is _
          let previous_char = chars[i-1];
          if (previous_char === '_') {
            tokenized_push.push({
              type: 'text',
              text: token
            });
            tokenized_push.push({
              type: 'var',
              val: '',
              var_index
            });

            token = '';
            var_index++;
          };
        };
      };

      return tokenized_push;

    },

  },

  methods: {

    changePushTemplate(template_index) {

      let account = this.$store.state.account;
      if (!account) { return };

      let push_info;
      if (this.push_templates) {
        push_info = this.push_templates[template_index]; // Get the push template info from the Account doc
      };

      let var_count = (push_info.text.match(/__/g) || []).length; // Count how many variables are in the push template text
      push_info.vars = Array(var_count).fill(''); // Initialize the push template variables
      let push_images = this.$store.state.account.push_images;
      if (push_images) {
        push_info.image = push_images[0];
      } else {
        push_info.image = 'https://storage.googleapis.com/outside-voice-a1d45.appspot.com/outside-voice-friends.jpeg';
      };
      this.push_vars = push_info.vars; // Init the push_vars in data, since the input auto-expand directive requires a v-model linked to data
      this.$store.commit('initStartingMessagePush', push_info); // Init the push template in the Study doc
      this.selected_push_template_index = template_index; // Set the local var selected_push_template_index (changes UI)
      this.$forceUpdate();
    },

    updateStartingMessagePushVar(event, var_index) {
      let var_text = event.target.value;
      this.$store.commit('updateStartingMessagePushVar', { var_text, var_index });
    },

    updateLinkStartingText(event) {
      this.$store.commit('updateLinkStartingText', event.target.value);
    },

    addImage(event) {

      analytics.track('New Survey Push Image Uploaded');

      // Load the image preview immediately
      let file = event.target.files[0];
      var reader = new FileReader();
      let that = this;

      reader.onload = (e) => {
        let local_path = e.target.result;
        let is_local = true;
        that.$store.commit('addStartingMessageImage', { image_url: local_path, is_local });
      };
      reader.readAsDataURL(file);

      // Compress and upload the file to GCS
      new Compressor(file, {
        quality: 0.7,
        convertSize: 500000, // Convert PNG's over 0.5mb to JPG's
        success(result) {
          let random_id = db_helper.getRandomID();
          let media_ref = that.$storage.child(random_id);
          media_ref.put(result).then((snapshot) => {
            let media_url = `https://storage.googleapis.com/outside-voice-a1d45.appspot.com/${random_id}`;
            let is_local = false;
            that.$store.commit('addStartingMessageImage', { image_url: media_url, is_local });
          });
        },
        error(err) {
          console.log(err.message);
          that.$store.commit('removeStartingMessageImage'); // TODO: Make this reset the starting image instead.
        }
      });

    },

    update() {
      this.$mount();
    },

  },


  mounted() {

    // Set initial survey starting message based on first account push_template
    if (!this.is_editing) {

      if (this.push_templates) {
        this.changePushTemplate(0);
      } else { // Account hasn't loaded yet - user is trying to refresh new survey page, wait 3 seconds to try again.
        setTimeout(() => {
          this.changePushTemplate(0);
        }, 3000);
      }

    // User is editing an existing survey, set data on component to reflect existing edits
    } else {

      let existing_starting_message = this.$store.state.new_survey.starting_message;

      // Set the template index that the existing survey was using
      let account = this.$store.state.account;
      if (account && account.push_templates) {
        let existing_push_id = existing_starting_message.push.id;
        let push_template_ids = _.map(account.push_templates, 'id');
        if (existing_push_id && push_template_ids) {
          this.selected_push_template_index = push_template_ids.indexOf(existing_push_id) || 0;
        };
      };

      // Set the push variables that the existing survey was using
      let existing_push_vars = existing_starting_message.push.vars;
      if (existing_push_vars) {
        this.push_vars = existing_push_vars;
      };

    }

  }

}
</script>

<style scoped lang="scss">

.starting-message-container{
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: "Montserrat", sans-serif;
  padding-top: 35px;
  padding-bottom: 35px;
  border-top: 1px solid rgb(220,220,220);
  border-bottom: 1px solid rgb(220,220,220);
  margin-bottom: 40px;
}

.title-box{
  h4{
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 25px;
    margin-right: 10px;
  }
  & > *{
    display: inline-block;
  }
  .anticon{
    opacity: .5;
    transition: all 0.2s ease;
    cursor: pointer;
    &:hover{
      opacity: .8;
    }
  }
}


.toggle-container{
  height: 38px;
  margin-bottom: 30px;
}

.toggle-inner{
  background-color: rgba(255,255,255,.15);
  height: 38px;
  width: 190px;
  border: 1px solid rgb(220,220,220);
  border-radius: 25px;
  position: relative;

  button{
    background-color: transparent;
    height: 35px;
    color: rgba(40,100,135, .6);
    font-size: 13px;
    font-weight: 600;
    border: none;
    transition: all 0.2s ease;
    cursor: pointer;
    position: absolute;
      top: 0px;
    z-index: 1;

    &:focus{
      outline: none;
    }
    &.selected{
      color: rgb(40,100,135);
      &:hover{
        color: rgb(40,100,135);
      }
    }
    &:hover{
      color: #286588;
    }
    background-size: 17px;
    background-position: right;
    background-repeat: no-repeat;
  }

  #push-message-btn{
    width: 90px;
    background-image: url("../../assets/whatsapp-logo.svg");
    background-position: 65px 9px;
    padding-left: 0px;
    left: 0px;
  }

  #link-message-btn{
    margin-left: 23px;
    width: 83px;
    left: 80px;
  }

  #selected-slider{
    background-color: white;
    width: 106px;
    height: 38px;
    position: absolute;
      top: -1px;
      left: -1px;
    border: 1px solid rgb(225,225,225);
    border-radius: 25px;
    box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.15);
    z-index: 0;
    transition: all 0.1s ease;
    &.link_selected{
      width: 96px;
      left: 94px;
    }
  }
}


.push-container{
  position: relative;
  .template-dropdown{
    position: absolute;
      left: -70px;
      top: 0px;
    .select-push-template-btn{
      height: 39px;
      width: 35px;
      background-color: transparent;
      background-image: url("../../assets/icons/select-push-template-btn.svg");
      background-repeat: no-repeat;
      background-position: center;
      background-size: 26px 26px;
      border: none;
      border-radius: 4px;
      transition: all 0.2s ease;
      &:hover{
        background-color: rgb(230,230,230);
        cursor: pointer;
      }
      &:focus{
        outline: none;
      }
    }
  }
  .push-content{
    width: 500px;
    p{
      font-size: 14px;
      font-weight: 600;
      display: inline;
      line-height: 38px;
      margin-right: 6px;
    }
    input{
      padding: 8px;
      margin-right: 5px;
    }
  }
}


.media-row{
  background-color: rgb(246,246,246);
  border-radius: 4px;
  height: 200px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;

  .media-container{
    height: 90%;
    min-width: 100px;
    background-color: white;
    margin-right: 10px;
    position: relative;
    overflow: hidden;
    border-radius: 4px;
    margin: 0 auto;
    img, video{
      display: flex;
      align-self: center;
      height: 100%;
      margin: 0 auto;
      border-radius: 4px;
    }
    .media-container-overlay{
      position: absolute;
        top: 0;
        left: 0;
      height: 100%;
      width: 100%;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2;
      button{
        margin-left: 6px;
        margin-right: 6px;
        background-repeat: no-repeat;
        background-color: transparent;
        background-position: center;
        border: none;
        transition: all 0.2s ease;
        opacity: 0;
        cursor: pointer;
        &:hover{
          opacity: 1 !important;
        }
        &:focus{
          outline: none;
        }
      }
      .media-preview-btn{
        height: 22px;
        width: 30px;
        background-size: 22px;
        background-image: url("../../assets/icons/media-preview-btn.svg");
      }
      .media-delete-btn{
        height: 20px;
        width: 20px;
        background-size: 20px;
        background-image: url("../../assets/icons/media-delete-btn.svg");
      }
      .change-image-btn{
        height: 65px;
        width: 150px;
        background-size: cover;
        background-image: url("../../assets/icons/change-image-btn.svg");
        background-repeat: no-repeat;
        background-position: center;
        z-index: 1;
        opacity: 0;
        cursor: pointer;
        transition: all 0.2s ease;
        // &:hover{
        //   transform: scale(1.03, 1.03);
        //   opacity: 1 !important;
        // }
      }
      &:hover{
        background-color: rgba(0,0,0,.3);
        .media-preview-btn,
        .media-delete-btn,
        .change-image-btn{
          opacity: .8;
          opacity: 1 !important;
          transform: scale(1.03, 1.03);
        }
      }
      &.darken{
        background-color: rgba(0,0,0,.5);
      }
      .uploading-spinner{
        position: absolute;
          top: 70px;
          left: 50%;
          transform: translateX(-17px);
        cursor: pointer;
      }
    }
  }

  // .choose-image-btn-container{
  //   height: 90%;
  //   width: 300px;
  //   background-color: rgb(242,242,242);
  //   border: 1px solid rgb(225,225,225);
  //   border-radius: 4px;
  //   display: flex;
  //   align-items: center;
  //   justify-content: center;
  //   margin: 0 auto;
  //
  //   .choose-image-btn{
  //     height: 65px;
  //     width: 150px;
  //     background-size: cover;
  //     background-image: url("../../assets/icons/choose-image-btn.svg");
  //     background-repeat: no-repeat;
  //     background-position: center;
  //     cursor: pointer;
  //     transition: all 0.2s ease;
  //     z-index: 1;
  //     &:hover{
  //       transform: scale(1.03, 1.03);
  //     }
  //   }
  // }


}


input[type="text"] {
  font-size: 14px;
  font-weight: 500;
  color: rgb(30,30,30);
  text-align: center;
  border: none;
  outline: none;
  width: 80%;
  background-color: white;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  &:focus{
    border: 1px solid #40a9ff;
    //outline: #40a9ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
}


input[type="file"] {
  display: none;
}


@media only screen
and (min-device-width : 320px)
and (max-device-width : 480px) {

.push-container{
  .push-content{
    width: 100%;
  }
}

input[type="text"] {
  width: 100%;
}


}

</style>
